import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowUpRight,
  BarChart3,
  CalendarDays,
  Lightbulb,
  School,
  TrendingUp,
  Users,
  Building2,
  Briefcase,
  DollarSign,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import PageHeader from "@/components/shared/PageHeader";
import DataExportButton from "@/components/shared/DataExportButton";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuthStore } from "@/store/authStore";
import { useAdminReports } from "@/hooks/useAdminData";
import { PageSkeleton } from "@/components/shared/Skeletons";

export const Route = createFileRoute("/admin/reports/")({
  component: AdminReportsPage,
});

const CHART_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

/** Backend data may carry its own colour; otherwise fall back to the chart palette. */
const sectorColor = (item: any, idx: number) => item?.color || CHART_COLORS[idx % CHART_COLORS.length];

const tooltipStyle = {
  borderRadius: 12,
  border: "1px solid var(--border)",
  background: "var(--popover)",
  color: "var(--popover-foreground)",
  fontSize: 12,
};

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-caption text-muted-foreground">
      <span className="size-2 rounded-full" style={{ background: color }} aria-hidden />
      {label}
    </span>
  );
}

function AdminReportsPage() {
  const [period, setPeriod] = useState("this-year");
  const { user } = useAuthStore();
  const { data: reportData, isLoading } = useAdminReports(period);

  const placementStats = user?.isDemo
    ? [
        { sector: "Banking & Finance", count: 245, percentage: 29, color: "var(--chart-1)" },
        { sector: "Technology", count: 198, percentage: 23, color: "var(--chart-2)" },
        {
          sector: "Media & Communications",
          count: 156,
          percentage: 18,
          color: "var(--chart-3)",
        },
        { sector: "Healthcare", count: 132, percentage: 16, color: "var(--chart-4)" },
        { sector: "Education", count: 116, percentage: 14, color: "var(--chart-5)" },
      ]
    : reportData?.placementStats || [];

  const monthlyData = user?.isDemo
    ? [
        { month: "Jan", students: 45, placements: 38 },
        { month: "Feb", students: 52, placements: 44 },
        { month: "Mar", students: 68, placements: 59 },
        { month: "Apr", students: 71, placements: 65 },
        { month: "May", students: 89, placements: 78 },
        { month: "Jun", students: 95, placements: 84 },
      ]
    : reportData?.monthlyData || [];

  const insights = user?.isDemo
    ? [
        {
          icon: <TrendingUp className="size-4" />,
          title: "Efficiency gains",
          description:
            "Placement cycle time reduced by 12 days since implementation of automated matching.",
        },
        {
          icon: <School className="size-4" />,
          title: "Institutional leaders",
          description: "UDSM and IFM accounts for 42% of platform activity this academic quarter.",
        },
        {
          icon: <ArrowUpRight className="size-4" />,
          title: "Partner sentiment",
          description:
            "Overall partner satisfaction score maintains a high average of 4.8/5.0 across sectors.",
        },
        {
          icon: <CalendarDays className="size-4" />,
          title: "Projected peak",
          description:
            "Historical data suggests a 25% increase in student activity for the upcoming June cycle.",
        },
      ]
    : reportData?.insights || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Intelligence & analytics"
        subtitle="Data-driven insights into platform growth, placement performance, and sector distribution."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Reports" }]}
        action={
          <>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="sm:w-36" aria-label="Reporting period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this-year">This year</SelectItem>
                <SelectItem value="last-year">Last year</SelectItem>
                <SelectItem value="all-time">All time</SelectItem>
              </SelectContent>
            </Select>
            <DataExportButton
              onExport={(format) => console.log("Exporting as", format)}
              label="Download full report"
            />
          </>
        }
      />

      {isLoading ? (
        <PageSkeleton />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-4">
            <StatCard
              label="Total student enrollment"
              value={user?.isDemo ? 1247 : reportData?.metrics?.total_students || 0}
              icon={<Users />}
              trend={user?.isDemo ? { value: "12%", positive: true } : undefined}
            />
            <StatCard
              label="Industry partnerships"
              value={user?.isDemo ? 89 : reportData?.metrics?.total_partners || 0}
              icon={<Building2 />}
              trend={user?.isDemo ? { value: "8%", positive: true } : undefined}
            />
            <StatCard
              label="Active placements"
              value={user?.isDemo ? 847 : reportData?.metrics?.active_placements || 0}
              icon={<Briefcase />}
              trend={user?.isDemo ? { value: "15%", positive: true } : undefined}
            />
            <StatCard
              label="Platform revenue (TZS)"
              value={user?.isDemo ? "124.5M" : reportData?.metrics?.revenue || "0"}
              icon={<DollarSign />}
              trend={user?.isDemo ? { value: "22%", positive: true } : undefined}
            />
          </div>

          <div className="grid gap-4 md:gap-6 lg:grid-cols-[1.6fr_1fr]">
            <SectionCard
              title="Monthly performance trends"
              actions={
                <div className="flex items-center gap-4">
                  <LegendDot color="var(--chart-1)" label="Enrollment" />
                  <LegendDot color="var(--chart-2)" label="Placements" />
                </div>
              }
            >
              {monthlyData.length > 0 ? (
                <div className="h-[300px] w-full pt-1">
                  <ResponsiveContainer>
                    <BarChart data={monthlyData} barGap={4} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12, fill: "var(--muted-foreground)" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                        axisLine={false}
                        tickLine={false}
                        width={36}
                      />
                      <RechartsTooltip
                        cursor={{ fill: "var(--muted)", opacity: 0.5 }}
                        contentStyle={tooltipStyle}
                        labelStyle={{ color: "var(--popover-foreground)" }}
                        itemStyle={{ color: "var(--body)" }}
                      />
                      <Bar
                        dataKey="students"
                        name="Enrollment"
                        radius={[8, 8, 0, 0]}
                        fill="var(--chart-1)"
                        maxBarSize={32}
                      />
                      <Bar
                        dataKey="placements"
                        name="Placements"
                        radius={[8, 8, 0, 0]}
                        fill="var(--chart-2)"
                        maxBarSize={32}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState
                  bare
                  icon={<BarChart3 />}
                  title="No trend data available"
                  description="Data for this period will appear once placements are recorded."
                />
              )}
            </SectionCard>

            <SectionCard title="Sector distribution">
              {placementStats.length > 0 ? (
                <div className="flex flex-col items-center gap-5 sm:flex-row lg:flex-col">
                  <div className="h-[180px] w-full shrink-0 sm:w-[180px] lg:w-full">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={placementStats}
                          dataKey="count"
                          nameKey="sector"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={78}
                          paddingAngle={2}
                          strokeWidth={0}
                        >
                          {placementStats.map((entry: any, idx: number) => (
                            <Cell key={idx} fill={sectorColor(entry, idx)} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          contentStyle={tooltipStyle}
                          itemStyle={{ color: "var(--body)" }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="w-full min-w-0 flex-1 space-y-3">
                    {placementStats.map((item: any, idx: number) => (
                      <li key={item.sector}>
                        <div className="mb-1 flex items-center justify-between gap-3">
                          <span className="flex min-w-0 items-center gap-2 text-sm text-body">
                            <span
                              className="size-2 shrink-0 rounded-full"
                              style={{ background: sectorColor(item, idx) }}
                              aria-hidden
                            />
                            <span className="truncate">{item.sector}</span>
                          </span>
                          <span className="flex items-baseline gap-2 tabular-nums">
                            <span className="text-sm text-foreground">{item.count}</span>
                            <span className="w-8 text-right text-caption text-muted-foreground">
                              {item.percentage}%
                            </span>
                          </span>
                        </div>
                        <div
                          className="h-1 overflow-hidden rounded-full bg-muted"
                          role="progressbar"
                          aria-label={`${item.sector} share`}
                          aria-valuenow={item.percentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.min(100, Math.max(0, item.percentage))}%`,
                              background: sectorColor(item, idx),
                            }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <EmptyState
                  bare
                  icon={<BarChart3 />}
                  title="No sector data"
                  description="No sector distribution data available."
                />
              )}
            </SectionCard>
          </div>

          <SectionCard
            title="Operational insights"
            actions={<Lightbulb className="size-4 text-muted-foreground" aria-hidden />}
          >
            {insights.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2 md:gap-4 xl:grid-cols-4">
                {insights.map((insight: any) => (
                  <article
                    key={insight.title}
                    className="h-full rounded-md border border-border bg-background p-4 transition-colors hover:border-hairline-strong"
                  >
                    <span
                      className="mb-3 flex size-8 items-center justify-center rounded-sm border border-border text-body [&_svg]:size-4"
                      aria-hidden
                    >
                      {insight.icon}
                    </span>
                    <h3 className="mb-1 text-sm font-medium text-foreground">{insight.title}</h3>
                    <p className="text-caption leading-5 text-muted-foreground">{insight.description}</p>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState
                bare
                icon={<Lightbulb />}
                title="No insights yet"
                description="No operational insights available at this time."
              />
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
}
